import { yupResolver } from "@hookform/resolvers/yup";
import {
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography as Type,
} from "@mui/material";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import CustomButton from "../../../../Components/CustomButton";
import Fonts from "../../../../Config/Fonts";
import Icons from "../../../../Config/Images";
import Strings from "../../../../Config/Strings";
import { fileDriveName } from "../../../../Constants/AppConstants";
import { productSchema } from "../../../../Services/ValidationSchema";
import {
  getAllCategorySubCategoryByUserIdAPI,
  getProductMeltingByStampAPI,
} from "../../../../Store/Action/productAction";
import { getProductCategoryDetailsAPI } from "../../../../Store/Action/productCategory";
import { getProductMetalDetailsAPI } from "../../../../Store/Action/productMetalAction";
import { getProductPurityDetailsAPI } from "../../../../Store/Action/productPurityAction";
import { getUserDetailsAPI } from "../../../../Store/Action/UserAction";
import Colors from "../../../../theme/Colors";
import { renderRequiredAsterisk } from "../../../../Utils/helper";
import { UploadMediaRequestAPI } from "../../../../Store/Action/mediaAction";
import { toast } from "react-toastify";

const ProductForm = ({ onSubmit }) => {
  const {
    watch,
    setValue,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(productSchema),
    defaultValues: {
      productName: "",
      productCategory: "",
      metalPurity: "",
      productDescription: "",
      grossWeight: "",
      lessWeight: "",
      productPrice: "",
      Quantity: "",
      productCode: "",
      productImage: null,
    },
    mode: "onBlur",
  });

  const dispatch = useDispatch();
  const inputRef = useRef();
  const [productCategoryData, setProductCategoryData] = useState([]);
  const [productMetalData, setProductMetalData] = useState([]);
  const [productPurityData, setProductPurityData] = useState([]);
  const [selectedMetal, setSelectedMetal] = useState("");
  const [selectedBrandUserId, setSelectedBrandUserId] = useState(null);
  const [filteredPurityOptions, setFilteredPurityOptions] = useState([]);
  const [brandData, setBrandData] = useState(null);
  const [imageSrc, setImageSrc] = useState("");
  const grossWeight = watch("grossWeight");
  const lessWeight = watch("lessWeight");
  const productMeltingPurity = watch("productMeltingPurity");
  const productWastageOfRetailer = watch("productWastageOfRetailer");
  const productWastageOfWholeSaler = watch("productWastageOfWholeSaler");
  const productNetWeight = watch("netWeight");
  const productRate = watch("retailerRate");
  const productWholeSalerRate = watch("wholesalerRate");
  const productBrandName = watch("productBrandName");
  const productCategory = watch("productCategory");
  const productSubCategory = watch("productSubCategory");
  const productStamp = watch("productStamp"); // Watch for changes in productStamp

  const { userData } = useSelector((state) => state.user);

  const fileDrive = fileDriveName.Product;

  const handleBrandChange = (e) => {
    const selectedBrand = brandData?.find(
      (brand) => brand.user_first_name === e.target.value
    );
    setSelectedBrandUserId(selectedBrand?.user_id || null); // Store the user_id associated with the selected brand
    setValue("productBrandName", e.target.value); // Update form state
  };

  const handleFormSubmit = (data) => {
    const selectedCategory = productCategoryData?.find(
      (cat) => cat?.product_category_name === data.productCategory
    );

    const formData = new FormData();
    Object.keys(data)?.forEach((key) => {
      if (key !== "productImage") {
        formData.append(key, data[key]);
      }
    });

    formData.append(
      "product_category_id",
      selectedCategory?.product_category_id || ""
    );
    formData.append("product_metal_purity_id", data.productMeltingPurity || "");

    // const filesArray = data?.productImage
    //   ? [
    //       {
    //         filename: data.productImage.name,
    //         path: data.productImage.path,
    //         size: data.productImage.size,
    //         type: data.productImage.type,
    //       },
    //     ]
    //   : [];

    // // Append the files array as a JSON string
    // formData.append("files", JSON.stringify(filesArray));
    onSubmit(formData);
  };

  const handleImageClick = useCallback(() => {
    inputRef.current.click();
  }, []);
  const handleImageChange = useCallback(
    async (e) => {
      const selectedAssets = e.target.files; // Get the selected files
  
      if (selectedAssets && selectedAssets.length > 0) {
        // Map the selected files to the expected request format
        const requestData = Array.from(selectedAssets).map((file) => ({
          uri: file.uri || URL.createObjectURL(file), // Ensure the uri is correctly set
          fileName: file.name, // Use the name property for the file name
          type: file.type || file.type, // File MIME type
          fileDriveName: 'Products', // Set the drive name
        }));
  

  
        try {
          const isMultiple = requestData?.length > 1; // Check if multiple files were selected
  
          // Dispatch the UploadMediaRequestAPI action
          await dispatch(
            UploadMediaRequestAPI(
              requestData, // Pass the structured request data
              'Products', // Drive name
              isMultiple,
              (isSuccess, result) => {
               
                if (isSuccess === true) {
                  toast.success(result?.data?.message);
                } else {
                  const errorMessage = result?.error?.message || 'Error occurred.';
                  toast.error(errorMessage);
                }
              },
            ),
          );
        } catch (error) {
          console.log('Error in file upload functionality:', error);
          toast.error('Something went wrong. Please try again.');
        }
      } else {
        console.warn('No files selected.');
      }
    },
    [dispatch] // Ensure 'dispatch' is in the dependency array
  );
  

  const [filteredSubCategories, setFilteredSubCategories] = useState([]);

  useEffect(() => {
    // Update filtered sub-categories whenever productCategory changes
    const selectedCategory = productCategoryData?.find(
      (cat) => cat?.product_category_name === productCategory
    );
    setFilteredSubCategories(selectedCategory?.sub_categories || []);
  }, [productCategory, productCategoryData]);

  // Add Prefix condition code in useEffect //
  useEffect(() => {
    // Calculate Net Weight
    if (grossWeight && lessWeight) {
      const netWeight = parseFloat(grossWeight) - parseFloat(lessWeight);
      setValue("netWeight", netWeight);
    }

    // Calculate Retailer Rate and Fine
    let productRate, productPerPcsFineRetailer;
    if (productMeltingPurity && productWastageOfRetailer) {
      productRate =
        parseFloat(productMeltingPurity) + parseFloat(productWastageOfRetailer);
      setValue("retailerRate", productRate);
    }

    if (productNetWeight && productRate) {
      productPerPcsFineRetailer = (
        (parseFloat(productNetWeight) * productRate) /
        100
      ).toFixed(3);
      setValue("perPcsFineRetailer", productPerPcsFineRetailer);
    }

    // Calculate Wholesaler Rate and Fine
    let productWholeSalerRate, productPerPcsFineWholeSaler;
    if (productMeltingPurity && productWastageOfWholeSaler) {
      productWholeSalerRate =
        parseFloat(productMeltingPurity) +
        parseFloat(productWastageOfWholeSaler);
      setValue("wholesalerRate", productWholeSalerRate);
    }

    if (productNetWeight && productWholeSalerRate) {
      productPerPcsFineWholeSaler = (
        (parseFloat(productNetWeight) * productWholeSalerRate) /
        100
      ).toFixed(3);
      setValue("perPcsFineWholeSaler", productPerPcsFineWholeSaler);
    }
  }, [
    grossWeight,
    lessWeight,
    productMeltingPurity,
    productNetWeight,
    productRate,
    productWastageOfRetailer,
    productWastageOfWholeSaler,
    productWholeSalerRate,
    setValue,
  ]);

  // if Admin then get all Products and not admin then get product By role id API call
  useEffect(() => {
    const params = {
      searchQuery: null || undefined,
      page: null || undefined,
      limit: null || undefined,
    };

    if (userData?.data?.user_role_name === Strings.Admin) {
      dispatch(
        getUserDetailsAPI({}, (result) => {
          setBrandData(result?.data);
        })
      );
    } else {
      dispatch(
        getProductCategoryDetailsAPI(params, (result) => {
          setProductCategoryData(result?.data);
        })
      );
    }

    dispatch(
      getProductMetalDetailsAPI(params, (result) =>
        setProductMetalData(result?.data)
      )
    );
    dispatch(
      getProductPurityDetailsAPI(params, (result) =>
        setProductPurityData(result?.data)
      )
    );
  }, [dispatch, userData?.data?.user_role_id, userData?.data?.user_role_name]);

  useEffect(() => {
    if (selectedMetal !== null) {
      const filteredPurity = productPurityData?.filter(
        (purity) => purity?.product_metal_name === selectedMetal
      );
      setFilteredPurityOptions(filteredPurity);
    }

    dispatch(
      getAllCategorySubCategoryByUserIdAPI(selectedBrandUserId, (result) => {
        return console.log("result called============", result);
      })
    );

    if (productStamp) {
      dispatch(
        getProductMeltingByStampAPI(productStamp, (result) => {
          if (result && result?.length > 0) {
            const meltingValue = result?.map(
              (e) => e?.stamp_melting_purity_value
            );
            setValue("productMeltingPurity", meltingValue);
          } else {
            setValue("productMeltingPurity", "");
          }
        })
      );
    } else {
      setValue("productMeltingPurity", ""); // Clear melting purity if stamp is cleared
    }
  }, [
    selectedMetal,
    productPurityData,
    dispatch,
    productStamp,
    setValue,
    selectedBrandUserId,
  ]);

  return (
    <form
      onSubmit={handleSubmit(handleFormSubmit)}
      style={{
        padding: 0,
        margin: 0,
        marginTop: "10px",
        display: "flex",
        justifyContent: "flex-start",
      }}
    >
      <Grid container spacing={3} xs={48}>
        <div style={{ width: "100%", display: "flex", marginTop: "35px" }}>
          <div
            style={{
              height: "100%",
              width: "41%",
              marginRight: 20,
              borderRadius: "4px",
              border: "1px dashed #D3D3D3",
              cursor: "pointer",
              display: "flex",
              overflow: "hidden",
              padding: 10,
              marginLeft: "30px",
            }}
          >
            <div
              onClick={handleImageClick}
              style={{ width: "50%", height: "100%" }}
            >
              {imageSrc ? (
                <img
                  src={imageSrc}
                  alt="Selected"
                  style={imageStyle}
                  loading="lazy"
                />
              ) : (
                <div
                  style={{
                    flexDirection: "column",
                    border: "1px solid #D3D3D3",
                    marginRight: "10px",
                    height: "100%",
                    width: "95%",
                    borderRadius: "4px",
                    justifyContent: "center",
                    alignItems: "center",
                    display: "flex",
                  }}
                >
                  <img
                    src={Icons.uploadImageIcon}
                    alt="Upload icon"
                    loading="lazy"
                    style={uploadIconStyle}
                  />
                  <input
                    type="file"
                    ref={inputRef}
                    multiple
                    style={{ display: "none" }}
                    onChange={(e) => handleImageChange(e)}
                  />
                </div>
              )}
            </div>

            <div style={{ width: "50%", height: "100%", position: "relative" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, 1fr)",
                  gap: "10px",
                  width: "100%",
                  height: "100%",
                }}
              >
                {Array(6)
                  .fill("")
                  .map((_, index) => (
                    <div
                      key={index}
                      style={{
                        width: "100%",
                        height: "74px",
                        backgroundColor: Colors.white,
                        border: "1px solid #D3D3D3",
                        borderRadius: 4,
                        position: "relative", // Set position to relative for proper icon positioning
                        display: "flex", // Center the icon within the div
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      {/* Add Plus Icon Here */}
                      <img
                        src={Icons.addImageIcon} // Ensure this is correctly defined or imported
                        alt="Add icon"
                        style={{ height: "24px", width: "24px" }}
                      />
                    </div>
                  ))}
              </div>
            </div>
          </div>

          <div style={{ width: "62%" }}>
            <Grid item xs={45} sm={12}>
              <Grid container spacing={0.9}>
                <Grid container spacing={1.5} xs={38}>
                  <Grid item xs={4}>
                    <Type variant="subtitle1" style={inputLabelFontStyle}>
                      Brand {renderRequiredAsterisk("productBrandName")}
                    </Type>
                    <Controller
                      name="productBrandName"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          error={!!errors.productBrandName}
                        >
                          <Select
                            {...field}
                            style={inputTextStyle}
                            displayEmpty
                            disabled={
                              userData?.data?.user_role_name !== Strings.Admin
                            }
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span>Select Product Brand</span>;
                              }
                              return selected;
                            }}
                            onChange={handleBrandChange}
                          >
                            {brandData?.map((brand) => (
                              <MenuItem
                                key={brand.user_id}
                                value={brand.user_first_name}
                              >
                                {brand.user_first_name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {errors.productBrandName?.message}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                  <Grid item xs={4}>
                    <Type variant="subtitle1" style={inputLabelFontStyle}>
                      Product Category{" "}
                      {renderRequiredAsterisk("productCategory")}
                    </Type>
                    <Controller
                      name="productCategory"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.productCategory}>
                          <Select
                            {...field}
                            style={inputTextStyle}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span>Select Product Category</span>;
                              }
                              return selected;
                            }}
                          >
                            {productCategoryData?.map((category) => (
                              <MenuItem
                                key={category.product_category_id}
                                value={category.product_category_name}
                              >
                                {category.product_category_name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {errors.productCategory?.message}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Type variant="subtitle1" style={inputLabelFontStyle}>
                      Product Sub-Category{" "}
                      {renderRequiredAsterisk("productSubCategory")}
                    </Type>
                    <Controller
                      name="productSubCategory"
                      control={control}
                      render={({ field }) => (
                        <FormControl
                          fullWidth
                          error={!!errors.productSubCategory}
                        >
                          <Select
                            {...field}
                            style={inputTextStyle}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span>Select Product Sub Category</span>;
                              }
                              return selected;
                            }}
                          >
                            {filteredSubCategories?.length > 0 ? (
                              filteredSubCategories.map((category) => (
                                <MenuItem
                                  key={category.product_category_id}
                                  value={category.product_category_name}
                                >
                                  {category.product_category_name}
                                </MenuItem>
                              ))
                            ) : (
                              <MenuItem disabled>
                                <span
                                  style={{
                                    fontSize: 14,
                                    fontWeight: "700",
                                    fontFamily: Fonts.Poppins,
                                    color: Colors.black,
                                  }}
                                >
                                  {Strings.NoDataFound}
                                </span>
                              </MenuItem>
                            )}
                          </Select>
                          <FormHelperText>
                            {errors.productSubCategory?.message}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container spacing={0.5} style={{ marginTop: "5px" }}>
                <Grid container spacing={1.5} xs={32}>
                  <Grid item xs={4}>
                    <Type variant="subtitle1" style={inputLabelFontStyle}>
                      Commission % {renderRequiredAsterisk("productCommission")}
                    </Type>
                    <Controller
                      name="productCommission"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Product Name"
                          variant="outlined"
                          fullWidth
                          error={!!errors.productCommission}
                          helperText={errors.productCommission?.message}
                          InputProps={{ style: inputTextStyle }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Type variant="subtitle1" style={inputLabelFontStyle}>
                      Product Code {renderRequiredAsterisk("productCode")}
                    </Type>
                    <Controller
                      name="productCode"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Product Code"
                          variant="outlined"
                          fullWidth
                          error={!!errors.productCode}
                          helperText={errors.productCode?.message}
                          InputProps={{ style: inputTextStyle, readOnly: true }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Type variant="subtitle1" style={inputLabelFontStyle}>
                      Product Name {renderRequiredAsterisk("productName")}
                    </Type>
                    <Controller
                      name="productName"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Product Name"
                          variant="outlined"
                          fullWidth
                          error={!!errors.productName}
                          helperText={errors.productName?.message}
                          InputProps={{ style: inputTextStyle }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container spacing={0.5} style={{ marginTop: "5px" }}>
                <Grid container spacing={1.5} xs={32}>
                  <Grid item xs={4}>
                    <Type variant="subtitle1" style={inputLabelFontStyle}>
                      Product Metal {renderRequiredAsterisk("productMetal")}
                    </Type>
                    <Controller
                      name="productMetal"
                      control={control}
                      render={({ field }) => (
                        <FormControl fullWidth error={!!errors.productMetal}>
                          <Select
                            {...field}
                            style={inputTextStyle}
                            displayEmpty
                            renderValue={(selected) => {
                              if (!selected) {
                                return <span>Select Product Metal</span>;
                              }
                              return selected;
                            }}
                          >
                            {productMetalData?.map((category) => (
                              <MenuItem
                                key={category?.product_metal_id}
                                value={category?.product_metal_name}
                              >
                                {category?.product_metal_name}
                              </MenuItem>
                            ))}
                          </Select>
                          <FormHelperText>
                            {errors.productMetal?.message}
                          </FormHelperText>
                        </FormControl>
                      )}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Type variant="subtitle1" style={bottomHeadingTextStyle}>
                      Stamp {renderRequiredAsterisk("productStamp")}
                    </Type>
                    <Controller
                      name="productStamp"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Stamp"
                          fullWidth
                          variant="outlined"
                          error={!!errors.productStamp}
                          helperText={errors.productStamp?.message}
                          InputProps={{ style: inputTextStyle }}
                        />
                      )}
                    />
                  </Grid>

                  <Grid item xs={4}>
                    <Type variant="subtitle1" style={bottomHeadingTextStyle}>
                      Melting (Purity){" "}
                      {renderRequiredAsterisk("productMeltingPurity")}
                    </Type>
                    <Controller
                      name="productMeltingPurity"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          placeholder="Product Melting"
                          variant="outlined"
                          fullWidth
                          error={!!errors.productMeltingPurity}
                          helperText={errors.productMeltingPurity?.message}
                          InputProps={{ style: inputTextStyle }}
                        />
                      )}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </div>
        </div>

        {/* Bottom Left: Gross, Less, and Net Weight */}
        <Grid
          container
          spacing={1}
          xs={40}
          style={{ marginLeft: "20px", marginTop: "8px" }}
        >
          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Gross Weight {renderRequiredAsterisk("grossWeight")}
            </Type>
            <Controller
              name="grossWeight"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Gross Weight"
                  variant="outlined"
                  fullWidth
                  error={!!errors.grossWeight}
                  helperText={errors.grossWeight?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Less Weight {renderRequiredAsterisk("lessWeight")}
            </Type>
            <Controller
              name="lessWeight"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Less Weight"
                  variant="outlined"
                  fullWidth
                  error={!!errors.lessWeight}
                  helperText={errors.lessWeight?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Net Weight
            </Type>
            <Controller
              name="netWeight"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Net Weight"
                  fullWidth
                  variant="outlined"
                  InputProps={{ style: inputTextStyle, disabled: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Quantity Pcs {renderRequiredAsterisk("productQuantity")}
            </Type>
            <Controller
              name="Quantity"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Product Quantity"
                  variant="outlined"
                  fullWidth
                  error={!!errors.Quantity}
                  helperText={errors.Quantity?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              HUID No. {renderRequiredAsterisk("productStamp")}
            </Type>
            <Controller
              name="productStamp"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Stamp"
                  variant="outlined"
                  error={!!errors.productStamp}
                  helperText={errors.productStamp?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Bottom Left: Gross, Less, and Net Weight */}
        <Grid
          container
          spacing={1}
          xs={40}
          style={{ marginLeft: "20px", marginTop: "6px" }}
        >
          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Wastage for Retailer{" "}
              {renderRequiredAsterisk("productWastageOfRetailer")}
            </Type>
            <Controller
              name="productWastageOfRetailer"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Wastage for Retailer"
                  variant="outlined"
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Rate {renderRequiredAsterisk("retailerRate")}
            </Type>
            <Controller
              name="retailerRate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Rate"
                  variant="outlined"
                  error={!!errors.retailerRate}
                  helperText={errors.retailerRate?.message}
                  InputProps={{ style: inputTextStyle, disabled: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Per Pcs Fine Retailers{" "}
              {renderRequiredAsterisk("perPcsFineRetailer")}
            </Type>
            <Controller
              name="perPcsFineRetailer"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Per Pcs Fine Retailers"
                  variant="outlined"
                  error={!!errors.perPcsFineRetailer}
                  helperText={errors.perPcsFineRetailer?.message}
                  InputProps={{ style: inputTextStyle, disabled: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              labour for Retailer {renderRequiredAsterisk("labourRetailer")}
            </Type>
            <Controller
              name="labourRetailer"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="labour Retailer"
                  variant="outlined"
                  error={!!errors.labourRetailer}
                  helperText={errors.labourRetailer?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Minimum Qty Retailer{" "}
              {renderRequiredAsterisk("minimumQtyRetailer")}
            </Type>
            <Controller
              name="minimumQuantityRetailer"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Minimum Quantity"
                  variant="outlined"
                  error={!!errors.minimumQuantityRetailer}
                  helperText={errors.minimumQuantityRetailer?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>
        </Grid>

        {/* Bottom Left: Gross, Less, and Net Weight */}
        <Grid
          container
          spacing={1}
          xs={40}
          style={{ marginLeft: "20px", marginTop: "6px" }}
        >
          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Wastage Wholesaler {renderRequiredAsterisk("wastageWholesaler")}
            </Type>
            <Controller
              name="productWastageOfWholeSaler"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Wastage Wholesalers"
                  variant="outlined"
                  error={!!errors.productWastageOfWholeSaler}
                  helperText={errors.productWastageOfWholeSaler?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Rate {renderRequiredAsterisk("wholesalerRate")}
            </Type>
            <Controller
              name="wholesalerRate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Rate"
                  variant="outlined"
                  fullWidth
                  error={!!errors.wholesalerRate}
                  helperText={errors.wholesalerRate?.message}
                  InputProps={{ style: inputTextStyle, disabled: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Per Pcs Fine Wholesalers{" "}
              {renderRequiredAsterisk("perPcsFineWholesaler")}
            </Type>
            <Controller
              name="perPcsFineWholeSaler"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="Per Pcs Fine Retailers"
                  variant="outlined"
                  error={!!errors.perPcsFineWholeSaler}
                  helperText={errors.perPcsFineWholeSaler?.message}
                  InputProps={{ style: inputTextStyle, disabled: true }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              labour Wholesaler {renderRequiredAsterisk("labourWholesaler")}
            </Type>
            <Controller
              name="labourWholesaler"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  placeholder="labour Wholesalers"
                  variant="outlined"
                  error={!!errors.labourWholesaler}
                  helperText={errors.labourWholesaler?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>

          <Grid item xs={2.4}>
            <Type variant="subtitle1" style={bottomHeadingTextStyle}>
              Minimum Qty Wholesaler{" "}
              {renderRequiredAsterisk("minimumQtyWholesaler")}
            </Type>
            <Controller
              name="minimumQuantityWholeSaler"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  placeholder="Minimum Quantity"
                  variant="outlined"
                  fullWidth
                  error={!!errors.minimumQuantityWholeSaler}
                  helperText={errors.minimumQuantityWholeSaler?.message}
                  InputProps={{ style: inputTextStyle }}
                />
              )}
            />
          </Grid>
        </Grid>

        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
          }}
        >
          <div style={{ width: "100%" }}>
            <Grid
              container
              spacing={1}
              item
              xs={10.5}
              style={{ marginTop: "5px", marginLeft: "30px" }}
            >
              <Type variant="subtitle1" style={inputLabelFontStyle}>
                Product Description
              </Type>
              <Controller
                name="productDescription"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    placeholder="Product Description"
                    variant="outlined"
                    fullWidth
                    multiline
                    maxRows={2} // You can adjust this value to control the maximum number of rows visible
                    error={!!errors.productDescription}
                    helperText={errors.productDescription?.message}
                    InputProps={{
                      style: {
                        ...inputTextStyle,
                        overflow: "auto",
                      }, // Adjust maxHeight as needed
                    }}
                  />
                )}
              />
            </Grid>
          </div>

          <div style={{ width: "50%" }}>
            <CustomButton handleSubmit={handleSubmit} label={Strings.Save} />
          </div>
        </div>
      </Grid>
    </form>
  );
};

const imageStyle = {
  height: "98%",
  width: "94%",
  objectFit: "cover",
  border: "1px solid #D3D3D3",
  borderRadius: 4,
};

const uploadIconStyle = {
  height: "40px",
  width: "40px",
};

const inputLabelFontStyle = {
  fontSize: 15,
  fontWeight: "600",
  fontFamily: Fonts.Poppins,
  color: Colors.black,
  display: "flex",
  justifyContent: "flex-start",
  marginBottom: "5px",
};

const bottomHeadingTextStyle = {
  fontSize: 16,
  fontWeight: "500",
  fontFamily: Fonts.Poppins,
  color: Colors.headingBlackColor,
  display: "flex",
  justifyContent: "flex-start",
  marginBottom: "5px",
};

const inputTextStyle = {
  fontSize: 14,
  fontWeight: "400",
  fontFamily: Fonts.Poppins,
  color: Colors.paginationTitle,
  display: "flex",
  justifyContent: "start",
  alignItems: "center",
  backgroundColor: Colors.white,
  textAlign: "start",
};

export default ProductForm;
